import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithPopup, signOut, OAuthProvider } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { ensureUserAndCompanyExist } from '../services/userService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import LoadingSpinner from '../components/common/LoadingSpinner';
import DashboardLoading from '../components/common/DashboardLoading';
import { validateEmailDomain, getUnauthorizedDomainError, getSSOProvider } from '../config/authorizedDomains';
import { 
  trackUserLogin, 
  trackUserLogout, 
  setUserAnalyticsProperties,
  trackError 
} from '../services/analyticsService';

// Initialize Microsoft OAuth provider
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({
  tenant: 'common', // Allows both personal and work accounts
  prompt: 'select_account'
});

interface CompanyData {
  id: string;
  name: string;
  domain: string;
  creditBalance: number;
  subscriptionPlan: string;
  subscriptionStatus: string;
}

interface UserData {
  id: string;
  email: string;
  name: string;
  companyId: string;
  role: string;
}

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  companyData: CompanyData | null;
  loading: boolean;
  dashboardLoading: boolean;
  loadingStep: number;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshCompanyData: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Initialize user data after successful authentication
  const initializeUserData = async (user: User) => {
    if (!user.email) return;

    try {
      setLoadingStep(1); // Step 1: Authenticating
      
      // Ensure user and company exist in Firestore
      await ensureUserAndCompanyExist(user);
      
      setLoadingStep(2); // Step 2: Loading company data
      
      // Parallel data fetching for better performance
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const newUserData: UserData = {
          id: user.uid,
          email: user.email,
          name: userData.name || user.displayName || user.email.split('@')[0],
          companyId: userData.companyId,
          role: userData.role || 'user'
        };
        setUserData(newUserData);
        
        // Fetch company data and any additional dashboard data in parallel
        const [companyDoc] = await Promise.all([
          getDoc(doc(db, 'companies', userData.companyId))
          // Add more parallel requests here if needed (e.g., recent transactions, notifications)
        ]);
        
        setLoadingStep(3); // Step 3: Preparing dashboard
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const newCompanyData: CompanyData = {
            id: userData.companyId,
            name: companyData.name,
            domain: companyData.domain,
            creditBalance: companyData.creditBalance || 0,
            subscriptionPlan: companyData.subscriptionPlan || 'free',
            subscriptionStatus: companyData.subscriptionStatus || 'active'
          };
          setCompanyData(newCompanyData);
        }
        
        // Small delay to show final step completion
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Track successful login
        if (newUserData && companyData) {
          setUserAnalyticsProperties(
            user.uid, 
            user.email, 
            userData.companyId, 
            userData.role || 'user'
          );
          
          try {
            // Use the same logic as authentication to determine provider
            // This ensures analytics data matches the actual SSO provider used
            // If domain is not configured, we skip tracking rather than guess
            const provider = getSSOProvider(user.email);
            trackUserLogin(provider, user.email, userData.companyId, user.uid);
          } catch (error) {
            console.error('Failed to track login - SSO provider not configured:', error);
            // Don't track login if provider is not properly configured
            // This prevents incorrect analytics data
          }
        }
      }
    } catch (error) {
      console.error('Error initializing user data:', error);
      setLoadingStep(2); // Reset to step 2 for fallback
      
      // Fallback to basic data extraction
      const emailDomain = user.email.split('@')[1];
      const companyName = emailDomain.split('.')[0];
      const companyId = emailDomain.replace(/\./g, '_').toLowerCase();
      
      const newCompanyData: CompanyData = {
        id: companyId,
        name: companyName.charAt(0).toUpperCase() + companyName.slice(1),
        domain: emailDomain,
        creditBalance: 0,
        subscriptionPlan: 'free',
        subscriptionStatus: 'active'
      };

      const newUserData: UserData = {
        id: user.uid,
        email: user.email,
        name: user.displayName || user.email.split('@')[0],
        companyId: companyId,
        role: 'admin'
      };

      setCompanyData(newCompanyData);
      setUserData(newUserData);
    }
  };

  const refreshCompanyData = async () => {
    if (!currentUser || !userData) return;
    
    try {
      // Fetch fresh company data from Firestore
      const companyRef = doc(db, 'companies', userData.companyId);
      const companyDoc = await getDoc(companyRef);
      
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        const refreshedCompanyData: CompanyData = {
          id: userData.companyId,
          name: companyData.name,
          domain: companyData.domain,
          creditBalance: companyData.creditBalance || 0,
          subscriptionPlan: companyData.subscriptionPlan || 'free',
          subscriptionStatus: companyData.subscriptionStatus || 'active'
        };
        setCompanyData(refreshedCompanyData);
      }
    } catch (error) {
      console.error('Error refreshing company data:', error);
    }
  };

  // Helper function to handle sign-in state management
  const handleSignInFlow = async (signInOperation: () => Promise<void>) => {
    try {
      setError(null);
      setDashboardLoading(true);
      setLoadingStep(1); // Only set to 1 when starting a new flow
      
      await signInOperation();
    } catch (error) {
      console.error('Sign-in error:', error);
      throw error;
    } finally {
      setDashboardLoading(false);
      // Don't reset loadingStep here - let it maintain the last step reached
      // The loading UI will be hidden anyway due to dashboardLoading=false
    }
  };

  const signInWithGoogle = async () => {
    await handleSignInFlow(async () => {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Validate domain before proceeding
      if (!validateEmailDomain(user.email || '')) {
        // Sign out the user immediately
        await signOut(auth);
        const errorMessage = getUnauthorizedDomainError(user.email || '');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Domain is authorized, proceed with initialization
      await initializeUserData(user);
    });
  };

  const signInWithMicrosoft = async () => {
    await handleSignInFlow(async () => {
      const result = await signInWithPopup(auth, microsoftProvider);
      const user = result.user;
      
      // Validate domain before proceeding
      if (!validateEmailDomain(user.email || '')) {
        // Sign out the user immediately
        await signOut(auth);
        const errorMessage = getUnauthorizedDomainError(user.email || '');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Domain is authorized, proceed with initialization
      await initializeUserData(user);
    });
  };

  const signInWithEmail = async (email: string) => {
    await handleSignInFlow(async () => {
      // Validate domain before attempting sign-in
      if (!validateEmailDomain(email)) {
        const errorMessage = getUnauthorizedDomainError(email);
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Get explicit SSO provider (no defaults)
      const provider = getSSOProvider(email);
      
      // Call the appropriate sign-in method (these will handle their own state management)
      if (provider === 'microsoft') {
        // Reset dashboard loading since the Microsoft sign-in will manage its own loading state
        setDashboardLoading(false);
        await signInWithMicrosoft();
      } else if (provider === 'google') {
        // Reset dashboard loading since the Google sign-in will manage its own loading state
        setDashboardLoading(false);
        await signInWithGoogle();
      } else {
        throw new Error('SSO provider not configured for this domain.');
      }
    });
  };

  const logout = async () => {
    try {
      // Track logout before signing out
      if (currentUser?.email) {
        trackUserLogout(currentUser.email);
      }
      
      await signOut(auth);
      // Clear local data on logout
      setUserData(null);
      setCompanyData(null);
      setError(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Validate domain for existing sessions (in case user was signed in before domain restrictions)
        if (!validateEmailDomain(user.email || '')) {
          console.warn('Unauthorized domain detected in existing session, signing out user');
          await signOut(auth);
          setUserData(null);
          setCompanyData(null);
          setLoading(false);
          return;
        }
        
        // Domain is authorized, initialize user/company data from Firestore
        await initializeUserData(user);
      } else {
        setUserData(null);
        setCompanyData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userData,
    companyData,
    loading,
    dashboardLoading,
    loadingStep,
    error,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithEmail,
    logout,
    refreshCompanyData,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <LoadingSpinner />
      ) : dashboardLoading ? (
        <DashboardLoading step={loadingStep} totalSteps={3} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
